<template>
  <div>
    <UiConfirm
      ref="confirm"
      :width="480"
      :type="confirm.confirmType"
      :action-name="confirm.actionName"
      :action-icon="confirm.actionIcon"
      :action-fa-icon="confirm.actionFaIcon"
      @save="onConfirm"
      @close="confirmClose"
    >
      <template v-slot:title>
        <div>
          {{ confirm.confirmTitle }}
        </div>
      </template>
      <div class="body__wrap">
        <i
          :class="confirm.confirmType"
          class="fas fa-exclamation-triangle"
        />
        <div
          class="body__msg t:ml-12"
          v-html="confirm.confirmMsg"
        />
      </div>
    </UiConfirm>
    <MonitoringPopup
      v-model:monitoring-info="monitoringInfo"
      :is-open="isMonitoringPopupOpen"
      @close="isMonitoringPopupOpen = false"
      @update:monitoring-info="getAffiliates"
    />
    <div class="affiliates-review affiliate-monitoring">
      <div
        class="toolbar"
        @keyup.enter="refreshHandler"
      >
        <div class="wrapper">
          <div class="section">
            <span class="title">{{ $t('affiliates.menu.monitored') }}</span>
            <UiInput
              v-model.trim="search"
              :width="180"
              class="search"
              :is-search="true"
              @search="refreshHandler"
            />
          </div>
          <div class="section">
            <UiPagination
              :page="page"
              :page-size="limit"
              :count="count"
              show-size-select
              @page-change="paginationHandler"
            />
          </div>
        </div>
      </div>
      <UiTable
        v-loading="isDataLoading"
        :fields="fields"
        :data="data"
        :rows-count="limit"
        :sort="{
          prop: sortProp,
          order: sortOrder,
        }"
        is-static
        :computed-row-class="getRowClass"
        lazy-loading
        class="table"
        i18n-path="affiliates.list"
        element-loading-custom-class="data-loader-spinner"
        @sort="sortingHandler"
      >
        <!--        TODO: убрать или прописать нужные настройки-->
        <template v-slot:append>
          <ElTableColumn
            width="110"
            :resizable="false"
            fixed="right"
            align="center"
            header-align="center"
          >
            <template v-slot="scope">
              <div class="row-value actions">
                <ActionIcon
                  icon="eye"
                  :router-to="getAffiliateLink(scope.row)"
                  :tooltip="$t('affiliates.monitored.infoButton')"
                />
                <ActionIcon
                  icon="pen"
                  :tooltip="$t('affiliates.monitored.edit_monitoring')"
                  @click="handleEditMonitoredClick(scope.row)"
                />
                <ActionIcon
                  icon="stop"
                  color="var(--text-negative)"
                  :tooltip="$t('affiliates.monitored.stop_monitoring')"
                  @click="handleStopMonitoredClick(scope.row)"
                />
              </div>
            </template>
          </ElTableColumn>
        </template>
      </UiTable>
    </div>
  </div>
</template>

<script>

import { mapGetters } from 'vuex';
import { differenceInSeconds } from 'date-fns';
import { pageSizeMixin, resolvePageSize } from '@/service/pageSize';
import MonitoringPopup from '@/components/Popups/MonitoringPopup.vue';
import queryParamsMixin from '../mixins/query-params';

const sizes = {
  1080: 20,
  1440: 30,
};

const viewName = 'affiliate/monitored/limit'; // для локалсторедж
const pageSize = resolvePageSize(viewName, {
  _default: 15,
  sizes,
});

export default {
  name: 'AffiliatesMonitored',
  components: { MonitoringPopup },
  mixins: [pageSizeMixin, queryParamsMixin],
  data() {
    return {
      isDataLoading: false,
      fields: [
        {
          name: 'affiliate_email',
          minWidth: '280',
          width: '320',
          align: 'left',
          sortable: 'custom',
          link: this.getAffiliateLink,
        },
        {
          name: 'reason',
          align: 'left',
          minWidth: '425',
          sortable: false,
        },
        {
          name: 'expires_at',
          width: '134',
          align: 'left',
          headerAlign: 'left',
          sortable: 'custom',
          format: 'date-time',
          class: 'expires-date',
        },
      ],
      confirm: {
        confirmMsg: '',
        confirmTitle: '',
        confirmType: '',
        actionName: '',
        actionFaIcon: '',
        data: {},
      },
      data: [],
      search: '',
      count: 0,
      permissions: {},
      page: 1,
      limit: pageSize,
      sortProp: 'expires_at',
      sortOrder: 'asc',
      monitoringInfo: null,
      isMonitoringPopupOpen: false,
    };
  },
  computed: {
    ...mapGetters({
      currentAcl: 'auth/currentAcl',
      adminAcl: 'auth/adminAcl',
    }),
    queryParams() {
      return {
        limit: this.limit,
        offset: (this.page - 1) * this.limit,
        search: this.search || undefined,
        sort_column: this.sortProp,
        sort_dir: this.sortOrder,
        ...this.options,
      };
    },
  },

  methods: {
    getRowClass({ row }) {
      return row.is_expired ? 'late' : '';
    },
    handleEditMonitoredClick(row) {
      this.monitoringInfo = this.$_.cloneDeep(row);
      this.isMonitoringPopupOpen = true;
    },
    handleStopMonitoredClick(row) {
      this.confirm.confirmMsg = `${this.$t('affiliates.monitored.stopPopup.text', { affiliateEmail: row.affiliate_email })}`;
      this.confirm.actionName = `${this.$t('affiliates.monitored.actionStop')}`;
      this.confirm.confirmTitle = `${this.$t('affiliates.monitored.stopPopup.title')}`;
      this.confirm.confirmType = 'warning';
      this.confirm.actionFaIcon = 'stop';
      this.confirm.data = row;
      this.$refs.confirm.open();
    },

    onConfirm() {
      this.$api.deleteAffiliateMonitoring(this.confirm.data.id)
        .then(() => {
          this.getAffiliates();
          this.$refs.confirm.close();
        });
    },
    confirmClose() {
      this.confirm.data = {};
    },

    async getAffiliates() {
      this.isDataLoading = true;

      const { data: { payload, misc } } = await this.$api.getAffiliateMonitoring(this.queryParams);

      this.count = misc.count;
      this.permissions = misc.permissions;
      this.data = payload;

      this.isDataLoading = false;
    },

    fetch() {
      return this.getAffiliates();
    },

    getAffiliateLink(row) {
      return `/affiliates/${row.affiliate_id}/info`;
    },

    paginationHandler(page, size) {
      if (this.page !== page || this.limit !== size) {
        this.page = page;
        this.limit = size;

        this.getAffiliates();
      }
    },

    sortingHandler({ prop, order }) {
      if (order === '') return;

      if (this.sortProp !== prop) {
        this.sortProp = prop;
      } else {
        this.sortOrder = order;
      }

      this.getAffiliates();
    },

    refreshHandler() {
      this.page = 1;

      this.getAffiliates();
    },
  },
};
</script>

<style lang="scss">
.affiliate-monitoring{
  .el-tooltip .row-value > span {
    display: block;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  }
  .el-table .el-table__row.late {
    background-color: #fff1f1 !important;
    &:hover{
      td{
        background-color: #fff1f1 !important;
      }
    }
    td{
      border-top: 1px solid #f1c7c7;
      border-bottom: 1px solid #f1c7c7;
      .row-value.expires-date{
        color: var(--text-negative);
      }
    }
    & + .late{
      td {
        border-top: none;
      }
    }
  }
}
</style>
